import type { UserLocation } from '@/hooks/useLocation'

export interface DistrictRecord {
    id: number
    name: string
    slug?: string
    price_multiplier?: number
}

export interface GooglePlaceLike {
    formatted_address?: string
    address_components?: Array<{
        long_name: string
        short_name: string
        types: string[]
    }>
    geometry?: {
        location?: {
            lat: () => number
            lng: () => number
        }
    }
}

const accentMap: Record<string, string> = {
    á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', ü: 'u', ñ: 'n',
}

export const normalizeText = (value: string): string => {
    return value
        .toLowerCase()
        .trim()
        .replace(/[áéíóúüñ]/g, match => accentMap[match] || match)
        .replace(/\s+/g, ' ')
}

export const findDistrictByName = (districts: DistrictRecord[], name?: string | null): DistrictRecord | null => {
    if (!name) return null
    const normalized = normalizeText(name)

    return (
        districts.find(d => normalizeText(d.name) === normalized)
        || districts.find(d => normalizeText(d.slug || '') === normalized)
        || null
    )
}

const componentTypePriority = [
    'sublocality_level_1',
    'sublocality',
    'locality',
    'administrative_area_level_2',
]

export const extractDistrictNameFromPlace = (place?: GooglePlaceLike | null): string | null => {
    if (!place?.address_components) return null

    for (const type of componentTypePriority) {
        const component = place.address_components.find(comp => comp.types.includes(type))
        if (component) {
            return component.long_name
        }
    }

    if (place.formatted_address) {
        const [firstPart] = place.formatted_address.split(',')
        return firstPart?.trim() || null
    }

    return null
}

export const buildUserLocationFromPlace = (
    place: GooglePlaceLike,
    districts: DistrictRecord[],
    fallback: Partial<UserLocation> = {}
): UserLocation | null => {
    const districtName = extractDistrictNameFromPlace(place)
    const matched = findDistrictByName(districts, districtName)

    if (!matched && !fallback.districtId) {
        return null
    }

    const lat = place.geometry?.location?.lat?.()
    const lng = place.geometry?.location?.lng?.()

    return {
        districtId: matched?.id ?? fallback.districtId ?? null,
        districtName: matched?.name ?? fallback.districtName ?? districtName ?? '',
        address: place.formatted_address || fallback.address || '',
        latitude: lat ?? fallback.latitude ?? null,
        longitude: lng ?? fallback.longitude ?? null,
        city: fallback.city || 'Lima',
        label: fallback.label || 'Mi ubicación',
        is_default: true,
    }
}
